import React, { useMemo, useCallback } from 'react';
import { createUseStyles, useTheme } from 'react-jss';
import { Theme } from '@app/theme';
import {
  CenteredModal,
  TextArea,
  Input,
  SubmitButton,
  useToast,
  Chip,
  Button,
  CheckboxesToArrayField,
} from 'components';
import * as Yup from 'yup';
import { Formik, Form } from 'formik';
import { ParticipationStatus, useContactSmsParticipation } from '@app/api';
import { getColorForParticipationStatus } from 'lib/participationUtils';
import { Trans, useTranslation } from 'react-i18next';

const useStyles = createUseStyles((theme: Theme) => ({
  submitButton: {
    textAlign: 'right',
  },
  button: {
    marginRight: theme.marginBase,
  },
}));

const validationSchema = Yup.object().shape({
  message: Yup.string().required(),
  status: Yup.array(),
});

interface SmsFormValues {
  message: string;
  status: ParticipationStatus[];
}

interface Props {
  onRequestClose: () => void;
  isOpen: boolean;
  sessionId: string;
  status: ParticipationStatus[];
  studyType?: string;
}

export const SmsContactColumnModal = ({
  onRequestClose,
  isOpen,
  status,
  sessionId,
  studyType,
}: Props) => {
  const theme: Theme = useTheme();
  const classes = useStyles({ theme });
  const toast = useToast();
  const { t } = useTranslation();

  const { mutateAsync: contactParticipation } = useContactSmsParticipation();

  const options = useMemo(
    () =>
      status.map((s) => ({
        label: t('sessions.details.monitoring.contactModal.filters.' + s, {
          context: studyType?.toLowerCase(),
        }),
        value: s,
      })),
    [status, studyType, t],
  );

  const onSubmit = useCallback(
    async (values: SmsFormValues) => {
      const { message, status } = values;

      try {
        await contactParticipation({
          sessionId,
          status,
          message,
        });
        toast.success(t('sessions.details.monitoring.contactModal.success'));
        onRequestClose();
      } catch (e) {
        toast.exception(e);
      }
    },
    [contactParticipation, sessionId, onRequestClose, toast, t],
  );

  const initialValues: SmsFormValues = useMemo(
    () => ({
      message: t('sessions.details.monitoring.contactModal.sms.template', {
        context: status[0],
      }),
      status: status,
    }),
    [status, t],
  );

  if (!status || !status.length) {
    return null;
  }

  return (
    <CenteredModal
      isOpen={isOpen}
      onRequestClose={onRequestClose}
      title={t('sessions.details.monitoring.contactModal.title')}
      large
      chip={
        <Chip color={getColorForParticipationStatus(status[0])}>
          <Trans
            i18nKey={`sessions.details.monitoring.columnTitle.${status[0]}`}
          />
        </Chip>
      }
    >
      <Formik
        initialValues={initialValues}
        onSubmit={onSubmit}
        validationSchema={validationSchema}
      >
        {({ values }) => (
          <Form>
            <TextArea
              name="message"
              label={t('sessions.details.monitoring.contactModal.sms.label')}
            />
            {status.length > 1 && (
              <CheckboxesToArrayField
                label={t(
                  'sessions.details.monitoring.contactModal.filters.title',
                )}
                options={options}
                name="status"
              />
            )}
            <div className={classes.submitButton}>
              <SubmitButton
                color="green"
                small
                text={t('sessions.details.monitoring.contactModal.submit')}
              />
            </div>
          </Form>
        )}
      </Formik>
    </CenteredModal>
  );
};
